import React from 'react';

import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import {
	type NamedPluginStatesFromInjectionAPI,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI, SelectionToolbarGroup } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { AnnotationPlugin } from './annotationPluginType';
import {
	setInlineCommentDraftState,
	setSelectedAnnotation,
	setPendingSelectedAnnotation,
	showInlineCommentForBlockNode,
} from './editor-commands';
import { annotationWithToDOMFix } from './nodeviews/annotationMark';
import { inlineCommentPlugin } from './pm-plugins/inline-comment';
import { keymapPlugin } from './pm-plugins/keymap';
import {
	buildSuppressedToolbar,
	buildToolbar,
	shouldSuppressFloatingToolbar,
} from './pm-plugins/toolbar';
import { ACTIONS } from './pm-plugins/types';
import {
	getPluginState,
	hasAnyUnResolvedAnnotationInPage,
	inlineCommentPluginKey,
	stripNonExistingAnnotations,
} from './pm-plugins/utils';
import type { AnnotationProviders } from './types';
import { InlineCommentView } from './ui/InlineCommentView';
import { getToolbarComponents } from './ui/toolbar-components';

export const annotationPlugin: AnnotationPlugin = ({ config: annotationProviders, api }) => {
	const featureFlags = api?.featureFlags?.sharedState.currentState();
	const isToolbarAIFCEnabled = Boolean(api?.toolbar);
	/**
	 * Allows external editor UI, currently AI suggestions, to safely close an inline comment
	 * without bypassing the provider's unsaved-content guard.
	 */
	const requestCloseInlineComment = async (): Promise<boolean> => {
		try {
			const requestClose = annotationProviders?.inlineComment.requestClose;
			if (!requestClose) {
				return true;
			}

			const canClose = await requestClose();

			if (!canClose) {
				return false;
			}

			return (
				api?.core.actions.execute(({ tr }) => {
					tr.setMeta(inlineCommentPluginKey, { type: ACTIONS.CLOSE_COMPONENT });
					return tr;
				}) ?? false
			);
		} catch {
			// Preserve the active comment if its close guard cannot complete.
			return false;
		}
	};

	if (isToolbarAIFCEnabled) {
		api?.toolbar?.actions.registerComponents(getToolbarComponents(api, annotationProviders));
	}

	return {
		name: 'annotation',

		marks() {
			return [
				{
					name: 'annotation',
					mark: annotationWithToDOMFix,
				},
			];
		},

		actions: {
			hasAnyUnResolvedAnnotationInPage,
			requestCloseInlineComment,
			stripNonExistingAnnotations,
			setInlineCommentDraftState: setInlineCommentDraftState(
				api?.analytics?.actions,
				annotationProviders?.inlineComment.supportedBlockNodes,
			),
			showCommentForBlockNode: showInlineCommentForBlockNode(
				annotationProviders?.inlineComment.supportedBlockNodes,
			),
			setSelectedAnnotation,
			setPendingSelectedAnnotation,
		},

		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			const pluginState = getPluginState(editorState) || undefined;
			const clonedPluginState = Object.assign({}, pluginState);
			delete clonedPluginState?.featureFlagsPluginState;
			return clonedPluginState;
		},

		pmPlugins: () => [
			{
				name: 'annotation',
				plugin: ({ dispatch }) => {
					if (annotationProviders) {
						return inlineCommentPlugin({
							dispatch,
							provider: annotationProviders.inlineComment,
							editorAnalyticsAPI: api?.analytics?.actions,
							featureFlagsPluginState: featureFlags,
							selectCommentExperience: annotationProviders.selectCommentExperience,
							viewInlineCommentTraceUFOPress: annotationProviders.viewInlineCommentTraceUFOPress,
							annotationManager: annotationProviders.annotationManager,
							api,
						});
					}

					return;
				},
			},
			{
				name: 'annotationKeymap',
				plugin: () => {
					if (annotationProviders) {
						return keymapPlugin(api);
					}
					return;
				},
			},
		],

		pluginsOptions: {
			floatingToolbar(state) {
				const pluginState = getPluginState(state);
				const bookmark = pluginState?.bookmark;

				if (shouldSuppressFloatingToolbar({ state, bookmark })) {
					return buildSuppressedToolbar(state);
				}
			},

			selectionToolbar(state, intl): SelectionToolbarGroup | undefined {
				if (isToolbarAIFCEnabled) {
					return;
				}

				if (!annotationProviders) {
					return;
				}

				const pluginState = getPluginState(state);
				if (
					pluginState &&
					pluginState.isVisible &&
					!pluginState.bookmark &&
					!pluginState.mouseData.isSelecting
				) {
					const { isToolbarAbove, onCommentButtonMount, getCanAddComments, contentType } =
						annotationProviders.inlineComment;

					const toolbarConfig = buildToolbar(api?.analytics?.actions)({
						state,
						intl,
						isToolbarAbove,
						api,
						createCommentExperience: annotationProviders.createCommentExperience,
						annotationManager: annotationProviders.annotationManager,
						onCommentButtonMount,
						getCanAddComments,
						contentType,
					});

					if (!toolbarConfig) {
						return undefined;
					} else {
						return {
							...toolbarConfig,
							rank: editorExperiment('platform_editor_controls', 'variant1') ? 1 : undefined,
						};
					}
				}
			},
		},

		contentComponent({ editorView, dispatchAnalyticsEvent }) {
			if (!annotationProviders || !editorView || isSSR()) {
				return null;
			}
			return (
				<AnnotationContentComponent
					api={api}
					editorView={editorView}
					annotationProviders={annotationProviders}
					dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				/>
			);
		},
	};
};

interface AnnotationContentComponentProps {
	annotationProviders: AnnotationProviders;
	api: ExtractInjectionAPI<typeof annotationPlugin> | undefined;
	dispatchAnalyticsEvent: DispatchAnalyticsEvent | undefined;
	editorView: EditorView;
}

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<typeof annotationPlugin>,
		'annotation'
	>,
) => {
	return {
		isVisible: states.annotationState?.isVisible,
		selectedAnnotations: states.annotationState?.selectedAnnotations,
		annotations: states.annotationState?.annotations,
	};
};

function AnnotationContentComponent({
	api,
	editorView,
	annotationProviders,
	dispatchAnalyticsEvent,
}: AnnotationContentComponentProps) {
	const annotationState = useSharedPluginStateWithSelector(api, ['annotation'], selector);

	// need to explicitly check for false as undefined is also a valid value to continue
	if (annotationState?.isVisible === false) {
		return null;
	}

	return (
		<div data-editor-popup="true">
			<InlineCommentView
				providers={annotationProviders}
				editorView={editorView}
				dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				editorAnalyticsAPI={api?.analytics?.actions}
				editorAPI={api}
			/>
		</div>
	);
}
